import React from "react";
// src/pages/__render.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import * as ReactDOM from "react-dom";
import type { Root } from "react-dom/client";
import * as ReactDOMClient from "react-dom/client";
import * as ReactJsxRuntime from "react/jsx-runtime";
import * as ReactJsxDevRuntime from "react/jsx-dev-runtime";
import {
  buildSandbox,
  EXTERNAL_DEPENDENCY_BLOCKED_PREFIX,
  SANDBOX_FORMAT,
} from "@/lib/sandboxBuild";

declare global {
  interface Window {
    __DAYONE_REACT_ROOT__?: Root;
    __DAYONE_PREVIEW_OK__?: boolean;
    __DAYONE_PREVIEW_ERROR__?: { message: string; stack?: string; componentStack?: string };
    __DAYONE_LAST_CONSOLE_ERRORS__?: string[];
    __DAYONE_BOOT_STEP__?: string;
    __DAYONE_LAST_ERROR__?: string;
    __DAYONE_RENDERED__?: boolean;
  }
}

/** Expose host React to sandbox bundle (no esm.sh / no network). */
function setSandboxReactGlobals() {
  (window as unknown as { __DAYONE_REACT__?: unknown }).__DAYONE_REACT__ = React;
  (window as unknown as { __DAYONE_REACT_DOM__?: unknown }).__DAYONE_REACT_DOM__ = ReactDOM;

  const originalCreateRoot = ReactDOMClient.createRoot.bind(ReactDOMClient);
  const createRootOnce = (rootContainer: HTMLElement | DocumentFragment) => {
    const rootEl = document.getElementById("root");
    const isRootContainer = rootEl && (rootContainer === rootEl || (rootContainer as HTMLElement).id === "root");
    if (isRootContainer && window.__DAYONE_REACT_ROOT__) {
      return window.__DAYONE_REACT_ROOT__;
    }
    const root = originalCreateRoot(rootContainer);
    if (isRootContainer) {
      window.__DAYONE_REACT_ROOT__ = root;
    }
    return root;
  };

  (window as unknown as { __DAYONE_REACT_DOM_CLIENT__?: typeof ReactDOMClient }).__DAYONE_REACT_DOM_CLIENT__ = {
    ...ReactDOMClient,
    createRoot: createRootOnce,
  };
  (window as unknown as { __DAYONE_REACT_JSX_RUNTIME__?: unknown }).__DAYONE_REACT_JSX_RUNTIME__ = ReactJsxRuntime;
  (window as unknown as { __DAYONE_REACT_JSX_DEV_RUNTIME__?: unknown }).__DAYONE_REACT_JSX_DEV_RUNTIME__ = ReactJsxDevRuntime;
}

type FileMap = Record<string, string>;

const FILES_KEY_PREFIX = "dayone:sandbox:files:";

function getSessionId(): string | null {
  try {
    const fromUrl = new URL(window.location.href).searchParams.get("sessionId");
    if (fromUrl) return fromUrl;
  } catch {
    /* ignore */
  }
  try {
    if (typeof localStorage !== "undefined") {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(FILES_KEY_PREFIX)) {
          return key.slice(FILES_KEY_PREFIX.length);
        }
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function isDebugMode(): boolean {
  try {
    return new URL(window.location.href).searchParams.get("debug") === "1";
  } catch {
    return false;
  }
}

// Post a structured message to all ancestor frames (reaches builder page regardless of nesting)
function postToAncestors(data: Record<string, unknown>) {
  try {
    let w: Window | null = window.parent;
    const origin = window.location.origin;
    while (w && w !== window) {
      w.postMessage(data, origin);
      if (w === w.parent) break;
      w = w.parent;
    }
  } catch {
    // ignore — cross-origin or detached frame
  }
}

function postLog(sessionId: string, level: string, message: string) {
  postToAncestors({
    type: "dayone:console",
    sessionId,
    level,
    message,
    ts: Date.now(),
  });
}

// ── AI Image Generation (post-render) ────────────────────────────
const IMAGE_GEN_CONCURRENCY = 2;
const IMAGE_GEN_MAX = 4;

/** Inject shimmer keyframes once into the sandbox document. */
function ensureShimmerStyles() {
  if (document.getElementById("dayone-shimmer-style")) return;
  const style = document.createElement("style");
  style.id = "dayone-shimmer-style";
  style.textContent = `
    @keyframes dayone-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .dayone-img-shimmer {
      background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
      background-size: 200% 100%;
      animation: dayone-shimmer 1.5s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

/** Scan the rendered DOM for data-generate-image imgs and replace with AI-generated images. */
async function processGeneratedImages(sid: string) {
  const imgs = document.querySelectorAll<HTMLImageElement>(
    "img[data-generate-image='true']"
  );
  if (imgs.length === 0) return;

  ensureShimmerStyles();
  const toProcess = Array.from(imgs).slice(0, IMAGE_GEN_MAX);
  postLog(sid, "info", `[dayone] Generating ${toProcess.length} AI image(s)…`);

  // Apply shimmer overlay while loading
  for (const img of toProcess) {
    img.style.position = "relative";
    img.classList.add("dayone-img-shimmer");
    img.dataset.originalSrc = img.src;
  }

  // Process in batches of IMAGE_GEN_CONCURRENCY
  // __render.tsx is part of the host SPA (same Vite build), so import.meta.env is available.
  const supabaseUrl =
    (typeof import.meta !== "undefined" ? (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL : undefined) ??
    document.querySelector<HTMLMetaElement>("meta[name='dayone-supabase-url']")?.content ??
    "";

  if (!supabaseUrl) {
    postLog(sid, "warn", "[dayone] No Supabase URL found — skipping image generation");
    for (const img of toProcess) img.classList.remove("dayone-img-shimmer");
    return;
  }

  const generateOne = async (img: HTMLImageElement) => {
    const prompt = img.dataset.prompt || img.alt || "image";
    const style = img.dataset.style || "photographic";
    const rect = img.getBoundingClientRect();
    const width = Math.round(rect.width) || 768;
    const height = Math.round(rect.height) || 512;

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, style, width, height }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.url) {
        // Preload the image before swapping to avoid flash
        const preload = new Image();
        preload.onload = () => {
          img.src = data.url;
          img.classList.remove("dayone-img-shimmer");
          img.removeAttribute("data-generate-image");
          postLog(sid, "info", `[dayone] Image generated: ${prompt.slice(0, 50)}`);
        };
        preload.onerror = () => {
          img.classList.remove("dayone-img-shimmer");
          postLog(sid, "warn", `[dayone] Failed to load generated image for: ${prompt.slice(0, 50)}`);
        };
        preload.src = data.url;
      } else {
        img.classList.remove("dayone-img-shimmer");
      }
    } catch (e) {
      img.classList.remove("dayone-img-shimmer");
      postLog(sid, "warn", `[dayone] Image generation failed: ${e}`);
    }
  };

  // Process in batches
  for (let i = 0; i < toProcess.length; i += IMAGE_GEN_CONCURRENCY) {
    const batch = toProcess.slice(i, i + IMAGE_GEN_CONCURRENCY);
    await Promise.allSettled(batch.map(generateOne));
  }
}

// Patch console methods once so user-code console.log() is forwarded to the builder.
// Also capture error/warn into __DAYONE_LAST_CONSOLE_ERRORS__ for display when root is empty.
const CONSOLE_ERROR_CAP = 10;
let consoleBridgeInstalled = false;
function installConsoleBridge(sessionId: string) {
  if (consoleBridgeInstalled) return;
  consoleBridgeInstalled = true;
  if (typeof window !== "undefined") {
    window.__DAYONE_LAST_CONSOLE_ERRORS__ = [];
  }

  const orig = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  /** Safely serialize a value for logging — handles Error objects properly */
  function safeSerialize(val: unknown): string {
    if (val === null) return "null";
    if (val === undefined) return "undefined";
    if (typeof val === "string") return val;

    // Error objects: extract message/stack (non-enumerable properties)
    if (val instanceof Error) {
      const parts = [val.name || "Error", val.message || "(no message)"];
      if (val.stack) parts.push(val.stack);
      return parts.join(": ");
    }

    // Objects with error-like shape (e.g., ErrorEvent, rejection reasons)
    if (typeof val === "object") {
      const obj = val as Record<string, unknown>;
      if (typeof obj.message === "string") {
        const parts = [String(obj.name || "Error"), obj.message];
        if (typeof obj.stack === "string") parts.push(obj.stack);
        return parts.join(": ");
      }
    }

    // Fallback: try JSON, then String
    try {
      const json = JSON.stringify(val, null, 2);
      // Detect empty object serialization — Error objects would produce "{}"
      if (json === "{}" || json === "[]") return String(val);
      return json;
    } catch {
      return String(val);
    }
  }

  function stringify(args: unknown[]): string {
    return args.map(safeSerialize).join(" ");
  }

  function captureErrorLevel(level: string, args: unknown[]) {
    const arr = typeof window !== "undefined" ? window.__DAYONE_LAST_CONSOLE_ERRORS__ : null;
    if (arr && (level === "error" || level === "warn")) {
      arr.push(`[${level}] ${stringify(args)}`);
      if (arr.length > CONSOLE_ERROR_CAP) arr.shift();
    }
  }

  console.log = (...args: unknown[]) => {
    orig.log(...args);
    postLog(sessionId, "log", stringify(args));
  };
  console.info = (...args: unknown[]) => {
    orig.info(...args);
    postLog(sessionId, "info", stringify(args));
  };
  console.warn = (...args: unknown[]) => {
    orig.warn(...args);
    captureErrorLevel("warn", args);
    postLog(sessionId, "warn", stringify(args));
  };
  console.error = (...args: unknown[]) => {
    orig.error(...args);
    captureErrorLevel("error", args);
    postLog(sessionId, "error", stringify(args));
  };
}

function normalizePath(p: string): string {
  let s = String(p ?? "").trim();
  if (s.startsWith("./")) s = s.slice(2);
  if (s.startsWith("/")) s = s.slice(1);
  return s;
}

const noop = { set(_: string) {}, append(_: string) {} };

function ensureHostOverlay() {
  // Only show the verbose debug overlay when ?debug=1 is in the URL
  if (!isDebugMode()) return noop;

  const id = "dayone-host-overlay";
  let el = document.getElementById(id) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.style.position = "fixed";
    el.style.left = "12px";
    el.style.top = "12px";
    el.style.zIndex = "2147483647";
    el.style.maxWidth = "min(760px, calc(100vw - 24px))";
    el.style.maxHeight = "min(360px, calc(100vh - 24px))";
    el.style.overflow = "auto";
    el.style.padding = "10px 12px";
    el.style.borderRadius = "12px";
    el.style.background = "rgba(0,0,0,0.8)";
    el.style.color = "#fff";
    el.style.fontFamily =
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
    el.style.fontSize = "12px";
    el.style.whiteSpace = "pre-wrap";
    el.style.boxShadow = "0 10px 30px rgba(0,0,0,0.35)";
    document.documentElement.appendChild(el);
  }
  return {
    set(text: string) {
      el!.textContent = text;
    },
    append(line: string) {
      el!.textContent = (el!.textContent || "") + line + "\n";
    },
  };
}

function ensureTailwindCdn() {
  if (document.getElementById("dayone-tailwind-cdn")) return;
  const script = document.createElement("script");
  script.id = "dayone-tailwind-cdn";
  script.src = "https://cdn.tailwindcss.com";
  document.head.appendChild(script);
}

function ensureSandboxDom() {
  // Tailwind CDN must be loaded before any app rendering so utility classes work
  ensureTailwindCdn();

  // Inject viewport-constraint reset BEFORE any app code renders.
  // Uses !important to override DayOne host CSS that loads after this stylesheet.
  // This forces ALL generated apps (old and new scaffolds) to respect the iframe viewport.
  if (!document.getElementById("dayone-viewport-reset")) {
    const resetStyle = document.createElement("style");
    resetStyle.id = "dayone-viewport-reset";
    resetStyle.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      html { height: 100% !important; margin: 0 !important; padding: 0 !important; overflow: auto !important; max-width: 100vw !important; }
      body { min-height: 100% !important; margin: 0 !important; padding: 0 !important; overflow: auto !important; max-width: 100vw !important; }
      #root { min-height: 100% !important; max-width: 100vw !important; overflow: visible !important; }
      section { padding-top: revert !important; padding-bottom: revert !important; }
    `;
    document.head.prepend(resetStyle);
  }

  // Disable browser scroll restoration — prevents restoring non-zero scroll positions
  // from previous __render loads.
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  window.scrollTo(0, 0);

  // The bootstrap script (BOOTSTRAP_APP_TSX) handles #root via getOrCreateRootEl() /
  // hardResetReactRoot(). We must NOT create a competing #sandbox-container + second #root
  // because that empty structure would sit below the app and create a large white gap.
  // Just ensure #root exists for the bootstrap to target.
  let root = document.getElementById("root") as HTMLElement | null;
  if (!root) {
    root = document.createElement("div");
    root.id = "root";
    root.style.height = "100%";
    document.body.appendChild(root);
  }
  if (isDebugMode()) console.log("[__render] dom check", { hasRoot: !!root });

  let css = document.getElementById("sandbox-css") as HTMLStyleElement | null;
  if (!css) {
    css = document.createElement("style");
    css.id = "sandbox-css";
    document.head.appendChild(css);
  }

  return { root, css };
}

function ensureStatusBar() {
  const id = "dayone-status-bar";
  let el = document.getElementById(id) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.style.position = "fixed";
    el.style.bottom = "0";
    el.style.left = "0";
    el.style.right = "0";
    el.style.zIndex = "2147483646";
    el.style.padding = "4px 10px";
    el.style.background = "rgba(0,0,0,0.85)";
    el.style.color = "#ccc";
    el.style.fontFamily =
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
    el.style.fontSize = "10px";
    el.style.lineHeight = "1.4";
    el.style.display = "flex";
    el.style.flexDirection = "column";
    el.style.gap = "2px";
    document.documentElement.appendChild(el);
  }
  return {
    update(info: {
      sessionId: string | null;
      files: number;
      status: string;
      error?: string;
      lsKey?: string;
      paths?: string[];
    }) {
      const time = new Date().toLocaleTimeString();
      const line1 = [
        `session: ${info.sessionId ?? "—"}`,
        `files: ${info.files}`,
        `${info.status}`,
        `@ ${time}`,
      ].join("  |  ");

      const line2 = info.paths?.slice(0, 5).join(", ") || info.lsKey || "";

      el!.innerHTML =
        `<div>${line1}</div>` +
        (line2
          ? `<div style="opacity:0.7">${line2}${info.paths && info.paths.length > 5 ? "..." : ""}</div>`
          : "");
      if (info.error) {
        el!.innerHTML +=
          `<div style="color:#fca5a5">err: ${escapeForHtml(info.error.slice(0, 100))}</div>`;
      }
      el!.style.color = info.error ? "#fca5a5" : "#ccc";
    },
  };
}

function escapeForHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function showErrorInRoot(title: string, detail: string) {
  console.error("[__render] showErrorInRoot:", title, detail);
  // Clean up skeleton overlay and progress pill on error
  const fadeOv = document.getElementById("dayone-fade-overlay");
  if (fadeOv) fadeOv.remove();
  const pillEl = document.getElementById("dayone-progress-pill");
  if (pillEl) { pillEl.style.opacity = "0"; pillEl.style.display = "none"; }
  const { root } = ensureSandboxDom();
  // Add id="dayone-error" so checkBuildState won't overwrite with skeleton
  root.innerHTML = `
    <div id="dayone-error" style="padding:32px;font-family:system-ui,sans-serif;color:#1a1a1a;">
      <div style="background:#fef2f2;border:1px solid #fca5a5;border-radius:12px;padding:20px 24px;max-width:640px;">
        <h2 style="margin:0 0 8px;font-size:18px;color:#b91c1c;">${title}</h2>
        <pre style="margin:0;white-space:pre-wrap;word-break:break-word;font-size:13px;color:#7f1d1d;line-height:1.5;">${escapeForHtml(detail)}</pre>
      </div>
    </div>`;
}

function showRuntimeErrorInRoot(message: string, stack?: string, eventType?: string) {
  console.error("[__render] showRuntimeErrorInRoot:", message, stack ?? "", eventType ?? "");
  const { root } = ensureSandboxDom();
  const parts: string[] = [escapeForHtml(message)];
  if (eventType) parts.push(`\nEvent type: ${escapeForHtml(eventType)}`);
  if (stack) parts.push(`\nStack:\n${escapeForHtml(stack)}`);
  // Add id="dayone-error" so checkBuildState won't overwrite with skeleton
  root.innerHTML = `
    <div id="dayone-error" style="padding:32px;font-family:system-ui,sans-serif;color:#1a1a1a;">
      <div style="background:#fef2f2;border:2px solid #dc2626;border-radius:12px;padding:20px 24px;max-width:720px;">
        <h2 style="margin:0 0 8px;font-size:18px;color:#b91c1c;">Runtime Error</h2>
        <pre style="margin:0;white-space:pre-wrap;word-break:break-word;font-size:13px;color:#7f1d1d;line-height:1.5;">${parts.join("")}</pre>
      </div>
    </div>`;
}

function showSkeletonInRoot() {
  const { root } = ensureSandboxDom();
  root.innerHTML = `
    <div id="dayone-skeleton" style="height:100%;background:#13132a;font-family:system-ui,-apple-system,sans-serif;overflow:hidden;">
      <style>
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .dn-sh{background:linear-gradient(90deg,#1a1a35 25%,#252547 50%,#1a1a35 75%);background-size:200% 100%;animation:shimmer 1.8s ease-in-out infinite;border-radius:8px;}
        .dn-sh2{background:linear-gradient(90deg,#1a1a35 25%,#252547 50%,#1a1a35 75%);background-size:200% 100%;animation:shimmer 2.4s ease-in-out infinite;border-radius:6px;}
      </style>
      <div style="height:56px;background:#0f0f24;border-bottom:1px solid #2a2a4a;display:flex;align-items:center;padding:0 20px;gap:16px;">
        <div class="dn-sh" style="width:120px;height:24px;"></div>
        <div style="flex:1;"></div>
        <div class="dn-sh" style="width:80px;height:28px;border-radius:14px;"></div>
        <div class="dn-sh" style="width:32px;height:32px;border-radius:50%;"></div>
      </div>
      <div style="display:flex;height:calc(100% - 56px);">
        <div style="width:220px;background:#0f0f24;border-right:1px solid #2a2a4a;padding:20px 16px;display:flex;flex-direction:column;gap:8px;">
          <div class="dn-sh2" style="width:100%;height:36px;margin-bottom:12px;"></div>
          <div class="dn-sh2" style="width:85%;height:28px;"></div>
          <div class="dn-sh2" style="width:70%;height:28px;"></div>
          <div class="dn-sh2" style="width:90%;height:28px;"></div>
          <div class="dn-sh2" style="width:60%;height:28px;"></div>
          <div style="flex:1;"></div>
          <div class="dn-sh2" style="width:100%;height:36px;"></div>
        </div>
        <div style="flex:1;padding:28px 32px;">
          <div class="dn-sh" style="width:240px;height:28px;margin-bottom:8px;"></div>
          <div class="dn-sh2" style="width:360px;height:16px;margin-bottom:28px;"></div>
          <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px;">
            <div style="background:#1a1a35;border:1px solid #2a2a4a;border-radius:12px;padding:20px;">
              <div class="dn-sh" style="width:100%;height:120px;margin-bottom:16px;border-radius:8px;"></div>
              <div class="dn-sh2" style="width:70%;height:18px;margin-bottom:8px;"></div>
              <div class="dn-sh2" style="width:100%;height:14px;margin-bottom:4px;"></div>
              <div class="dn-sh2" style="width:85%;height:14px;"></div>
            </div>
            <div style="background:#1a1a35;border:1px solid #2a2a4a;border-radius:12px;padding:20px;">
              <div class="dn-sh" style="width:100%;height:120px;margin-bottom:16px;border-radius:8px;"></div>
              <div class="dn-sh2" style="width:60%;height:18px;margin-bottom:8px;"></div>
              <div class="dn-sh2" style="width:90%;height:14px;margin-bottom:4px;"></div>
              <div class="dn-sh2" style="width:75%;height:14px;"></div>
            </div>
            <div style="background:#1a1a35;border:1px solid #2a2a4a;border-radius:12px;padding:20px;">
              <div class="dn-sh" style="width:100%;height:120px;margin-bottom:16px;border-radius:8px;"></div>
              <div class="dn-sh2" style="width:80%;height:18px;margin-bottom:8px;"></div>
              <div class="dn-sh2" style="width:95%;height:14px;margin-bottom:4px;"></div>
              <div class="dn-sh2" style="width:65%;height:14px;"></div>
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

function ensureProgressPill() {
  const id = "dayone-progress-pill";
  let el = document.getElementById(id) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.style.cssText = "position:fixed;bottom:16px;left:16px;z-index:2147483647;background:rgba(0,0,0,0.6);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);color:#fff;font-family:system-ui,-apple-system,sans-serif;font-size:13px;font-weight:500;border-radius:9999px;padding:6px 14px;display:none;align-items:center;gap:8px;transition:opacity 0.3s ease;pointer-events:none;";
    document.documentElement.appendChild(el);
  }
  return {
    show(text: string) {
      el!.innerHTML = `<span style="width:14px;height:14px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;display:inline-block;animation:spin 0.8s linear infinite;flex-shrink:0;"></span><span>${text}</span>`;
      el!.style.opacity = "1";
      el!.style.display = "flex";
    },
    hide() {
      el!.style.opacity = "0";
      setTimeout(() => { if (el) el.style.display = "none"; }, 300);
    },
  };
}

function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return String(h);
}

// Bootstrap: history fix for /__render, hard reset #root, then createRoot + render
const BOOTSTRAP_APP_TSX = [
  'import React from "react";',
  'import { createRoot } from "react-dom/client";',
  'import * as AppModule from "./App.tsx";',
  'const mod = AppModule as any;',
  'let App = mod.default ?? mod.App;',
  'if (App && typeof App === "object" && typeof App.default === "function") App = App.default;',
  'if (typeof App !== "function" && typeof App !== "object") throw new Error("App.tsx must export default or named App");',
  "",
  "const __debug = new URLSearchParams(typeof window !== \"undefined\" ? window.location.search : \"\").get(\"debug\") === \"1\";",
  "const dlog = (...args: unknown[]) => { if (__debug) console.log(\"[sandbox]\", ...args); };",
  "const W = typeof window !== \"undefined\" ? (window as Record<string, unknown>) : {};",
  "",
  "W.__DAYONE_BOOT_STEP__ = \"init\";",
  "W.__DAYONE_RENDERED__ = false;",
  "W.__DAYONE_LAST_ERROR__ = undefined;",
  "const TELEMETRY_KEY_PREFIX = \"dayone:sandbox:telemetry:\";",
  "function __getSidFromUrl() { try { return (typeof window !== \"undefined\" && new URLSearchParams(window.location.search).get(\"sessionId\")) || null; } catch { return null; } }",
  "function __postTelemetry(obj) {",
  "  try {",
  "    const sid = __getSidFromUrl() ?? obj.sessionId ?? null;",
  "    const tsVal = Date.now();",
  "    const data = { type: \"DAYONE_PREVIEW_TELEMETRY\", sessionId: sid || \"unknown\", ts: tsVal, bootStep: W.__DAYONE_BOOT_STEP__, lastError: W.__DAYONE_LAST_ERROR__, rendered: W.__DAYONE_RENDERED__ === true, error: (W.__DAYONE_PREVIEW_ERROR__ && W.__DAYONE_PREVIEW_ERROR__.message) || W.__DAYONE_LAST_ERROR__ || null, url: typeof location !== \"undefined\" ? location.href : \"\", ...obj };",
  "    if (typeof window !== \"undefined\" && window.parent !== window) window.parent.postMessage(data, window.location.origin);",
  "    if (sid) {",
  "      try {",
  "        localStorage.setItem(TELEMETRY_KEY_PREFIX + sid, JSON.stringify({ ...data, sessionId: sid, ts: tsVal, source: \"__render\" }));",
  "        if (__debug) console.log(\"[sandbox] telemetry localStorage\", sid, W.__DAYONE_BOOT_STEP__);",
  "      } catch (_) {}",
  "    }",
  "  } catch (_) {}",
  "}",
  "function __sendAck(nonce, pingSessionId) {",
  "  const curSid = __getSidFromUrl();",
  "  const respSid = curSid || pingSessionId || \"unknown\";",
  "  const ack = { type: \"DAYONE_RENDER_ACK\", sessionId: respSid, nonce, bootStep: W.__DAYONE_BOOT_STEP__, rendered: W.__DAYONE_RENDERED__ === true, lastError: W.__DAYONE_LAST_ERROR__, sessionIdUsed: curSid, pingSessionId: pingSessionId || null };",
  "  if (typeof window !== \"undefined\" && window.parent !== window) window.parent.postMessage(ack, window.location.origin);",
  "  __postTelemetry({ sessionId: respSid, rendered: W.__DAYONE_RENDERED__ === true, error: (W.__DAYONE_PREVIEW_ERROR__ && W.__DAYONE_PREVIEW_ERROR__.message) || W.__DAYONE_LAST_ERROR__ || null });",
  "}",
  "if (typeof window !== \"undefined\") {",
  "  window.addEventListener(\"message\", function(ev) {",
  "    const d = ev.data;",
  "    if (!d) return;",
  "    const isPing = d.type === \"DAYONE_PREVIEW_PING\" && d.sessionId;",
  "    const isHello = d.type === \"DAYONE_HOST_HELLO\" && d.sessionId;",
  "    if (!isPing && !isHello) return;",
  "    const pingSid = String(d.sessionId);",
  "    const nonce = d.nonce ?? d.ts ?? Date.now();",
  "    __sendAck(nonce, pingSid);",
  "  });",
  "}",
  "",
  "if (typeof window !== \"undefined\") {",
  "  window.onerror = (msg, src, line, col, err) => {",
  "    const stack = err && err.stack ? String(err.stack) : undefined;",
  "    W.__DAYONE_LAST_ERROR__ = String(msg) + (stack ? \"\\n\" + stack : \"\");",
  "    __postTelemetry({ rendered: false, error: String(msg), bootStep: W.__DAYONE_BOOT_STEP__, lastError: W.__DAYONE_LAST_ERROR__, stack });",
  "    return false;",
  "  };",
  "  window.onunhandledrejection = (e) => {",
  "    const s = e.reason != null ? String(e.reason) : \"unknown\";",
  "    W.__DAYONE_LAST_ERROR__ = \"unhandledrejection: \" + s;",
  "    __postTelemetry({ rendered: false, error: \"unhandledrejection: \" + s, bootStep: W.__DAYONE_BOOT_STEP__, lastError: W.__DAYONE_LAST_ERROR__ });",
  "  };",
  "}",
  "",
  "try {",
  "  W.__DAYONE_BOOT_STEP__ = \"before_replaceState\";",
  "  const urlSid = __getSidFromUrl();",
  "  dlog(\"boot\", \"url=\", typeof location !== \"undefined\" ? location.href : \"\", \"sessionIdFromUrl=\", urlSid);",
  "  // Replace /__render?sessionId=... with / so BrowserRouter matches the default route.",
  "  // This is safe: we're inside a sandboxed iframe, so replaceState only affects the iframe's URL.",
  "  if (typeof history !== \"undefined\" && typeof history.replaceState === \"function\") {",
  "    history.replaceState({}, \"\", \"/\");",
  "    dlog(\"replaceState → /\");",
  "  }",
  "} catch (e) {",
  "  dlog(\"history.replaceState failed\", e);",
  "}",
  "",
  "function getOrCreateRootEl(): HTMLElement {",
  "  const existing = document.getElementById(\"root\") as HTMLElement | null;",
  "  if (existing) return existing;",
  "  const el = document.createElement(\"div\");",
  "  el.id = \"root\";",
  "  document.body.appendChild(el);",
  "  return el;",
  "}",
  "",
  "function hardResetReactRoot(): HTMLElement {",
  "  W.__DAYONE_BOOT_STEP__ = \"before_hardReset\";",
  "  const prevRoot = W.__DAYONE_REACT_ROOT__ as { unmount?: () => void } | undefined;",
  "  if (prevRoot && typeof prevRoot.unmount === \"function\") {",
  "    try { dlog(\"Unmounting previous React root\"); prevRoot.unmount(); } catch (e) { dlog(\"Unmount failed\", e); }",
  "  }",
  "  W.__DAYONE_REACT_ROOT__ = null;",
  "",
  "  const rootEl = getOrCreateRootEl();",
  "  const fresh = rootEl.cloneNode(false) as HTMLElement;",
  "  rootEl.replaceWith(fresh);",
  "  dlog(\"Replaced #root node to clear React container markers\");",
  "  return fresh;",
  "}",
  "",
  "let rootEl: HTMLElement;",
  "try { rootEl = hardResetReactRoot(); }",
  "catch (e) { dlog(\"hardResetReactRoot failed, falling back\", e); rootEl = getOrCreateRootEl(); }",
  "",
  "W.__DAYONE_BOOT_STEP__ = \"before_createRoot\";",
  "const root = createRoot(rootEl);",
  "W.__DAYONE_REACT_ROOT__ = root;",
  "W.__DAYONE_BOOT_STEP__ = \"after_createRoot\";",
  "",
  "const DayOneRenderTracker = ({ children }: { children: React.ReactNode }) => {",
  "  React.useEffect(() => {",
  "    W.__DAYONE_RENDERED__ = true;",
  "    __postTelemetry({ rendered: true, bootStep: W.__DAYONE_BOOT_STEP__, lastError: W.__DAYONE_LAST_ERROR__, url: typeof location !== \"undefined\" ? location.href : \"\" });",
  "  }, []);",
  "  return children;",
  "};",
  "class DayOneErrorBoundary extends React.Component<{children:React.ReactNode},{hasError:boolean,error:Error|null}> {",
  "  state = { hasError: false, error: null as Error | null };",
  "  static getDerivedStateFromError(e: Error) { return { hasError: true, error: e }; }",
  "  componentDidCatch(e: Error, info: React.ErrorInfo) {",
  "    W.__DAYONE_PREVIEW_ERROR__ = { message: e.message, stack: e.stack, componentStack: info?.componentStack };",
  "    __postTelemetry({ rendered: false, error: e.message, bootStep: W.__DAYONE_BOOT_STEP__, lastError: W.__DAYONE_LAST_ERROR__, stack: e.stack });",
  "  }",
  "  render() {",
  "    if (this.state.hasError) return React.createElement(\"div\", { style: { padding: 24, fontFamily: \"system-ui\", color: \"#b91c1c\" } }, \"Error: \" + (this.state.error?.message ?? \"unknown\"));",
  "    return this.props.children;",
  "  }",
  "}",
  "",
  "W.__DAYONE_BOOT_STEP__ = \"before_render\";",
  "root.render(React.createElement(DayOneErrorBoundary, null, React.createElement(DayOneRenderTracker, null, React.createElement(App))));",
  "W.__DAYONE_BOOT_STEP__ = \"after_render\";",
  "dlog(\"Rendered App()\");",
  "",
  "// Increased from 1500ms to 5000ms for complex apps with many components",
  "setTimeout(() => {",
  "  if (W.__DAYONE_RENDERED__ !== true) {",
  "    const step = W.__DAYONE_BOOT_STEP__ ?? \"?\";",
  "    const err = W.__DAYONE_LAST_ERROR__ ?? \"(none)\";",
  "    __postTelemetry({ rendered: false, error: \"Preview blank\", bootStep: step, lastError: String(err).slice(0, 500) });",
  "    const el = document.createElement(\"div\");",
  "    el.id = \"dayone-blank-overlay\";",
  "    el.style.cssText = \"position:fixed;inset:0;z-index:999999;background:rgba(0,0,0,0.92);color:#fff;font-family:system-ui,sans-serif;padding:24px;overflow:auto;\";",
  "    el.innerHTML = \"<h2 style=margin:0 0 12px;font-size:18px>Preview blank</h2><p style=margin:0 0 8px>BootStep: \" + step + \"</p><p style=margin:0 0 8px>Error: \" + (\"\" + err).slice(0, 500) + \"</p><p style=margin:16px 0 0;font-size:13px;opacity:0.8>Open DevTools Console (F12) for details.</p>\";",
  "    document.body.appendChild(el);",
  "  }",
  "}, 5000);",
].join("\n");

function bootstrapSanityCheck(content: string): void {
  if (!isDebugMode()) return;
  const singles = (content.match(/'/g) ?? []).length;
  const doubles = (content.match(/"/g) ?? []).length;
  if (singles % 2 !== 0 || doubles % 2 !== 0) {
    console.warn("[__render] bootstrap quote sanity: singles=" + singles + " doubles=" + doubles);
  }
}

export default function __Render() {
  const sessionId = useMemo(getSessionId, []);
  const url = typeof window !== "undefined" ? window.location.href : "";
  let cacheBust: string | null = null;
  try {
    if (typeof window !== "undefined") {
      const sp = new URL(url).searchParams;
      cacheBust = sp.get("t") ?? sp.get("k");
    }
  } catch {
    /* ignore */
  }
  if (isDebugMode()) console.log("[__render] mounted", { url, sessionId, cacheBust });

  const [status, setStatus] = useState<string>("Idle");
  const [errorText, setErrorText] = useState<string>("");
  const blobRef = useRef<string | null>(null);
  const lastHashRef = useRef<string>("");
  const buildingRef = useRef(false);
  const lastRuntimeErrorRef = useRef<{ message: string; stack?: string; eventType: string } | null>(null);

  function cleanupScript() {
    const old = document.getElementById("dayone-render-script");
    if (old) old.remove();
    const blob = blobRef.current;
    if (typeof blob === "string") {
      try {
        URL.revokeObjectURL(blob);
      } catch (_) { /* ignore */ }
      blobRef.current = null;
    }
    // Do not clear root.innerHTML; bootstrap reuses __DAYONE_REACT_ROOT__ and will call render()
  }

  async function rebuild() {
    // Concurrency guard — skip if a build is already in progress
    if (buildingRef.current) return;
    buildingRef.current = true;

    try {
      await rebuildInner();
    } finally {
      buildingRef.current = false;
    }
  }

  async function rebuildInner() {
    const overlay = ensureHostOverlay();
    const bar = ensureStatusBar();
    overlay.set(`[host] starting…\nsessionId=${sessionId ?? "(null)"}`);
    bar.update({ sessionId, files: 0, status: "Starting…" });

    if (!sessionId) {
      setStatus("Waiting");
      overlay.append(`[host] No sessionId in URL; no cached files in localStorage`);
      bar.update({ sessionId: null, files: 0, status: "Waiting for session…" });
      const { root } = ensureSandboxDom();
      root.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;font-family:system-ui,sans-serif;color:#374151;text-align:center;">
          <h2 style="margin:0 0 8px;font-size:18px;font-weight:600;">No preview session</h2>
          <p style="margin:0;font-size:14px;color:#6b7280;max-width:400px;">No sessionId in URL and no cached files in localStorage.</p>
          <p style="margin:12px 0 0;font-size:13px;color:#9ca3af;">Open the builder and run preview to load your app.</p>
        </div>`;
      return;
    }

    const lsKey = `dayone:sandbox:files:${sessionId}`;
    const raw = localStorage.getItem(lsKey);
    if (isDebugMode()) {
      console.log(`[PREVIEW READ] sessionId=${sessionId} lsKey=${lsKey} rawLen=${raw?.length ?? 0}`);
    }
    if (!raw) {
      setStatus("Waiting");
      setErrorText("");
      overlay.append(`[host] No files yet at ${lsKey}, waiting...`);
      bar.update({ sessionId, files: 0, status: "Waiting for files…", lsKey });
      showSkeletonInRoot();
      return;
    }

    let files: FileMap;
    try {
      const parsed = JSON.parse(raw);
      files = Array.isArray(parsed)
        ? Object.fromEntries(
          parsed.map((f) => [normalizePath(f.name), String(f.content ?? "")]),
        )
        : parsed;

      const normalized: FileMap = {};
      for (const [k, v] of Object.entries(files)) {
        normalized[normalizePath(k)] = String(v ?? "");
      }
      files = normalized;
    } catch (e: any) {
      const msg = `JSON parse error: ${String(e?.message || e)}`;
      setStatus("Error");
      setErrorText(msg);
      overlay.append(`[host] ${msg}`);
      bar.update({ sessionId, files: 0, status: "Error", error: msg });
      showErrorInRoot("File Parse Error", msg);
      return;
    }

    const fileCount = Object.keys(files).length;
    const filePaths = Object.keys(files);
    if (isDebugMode()) console.log(`[PREVIEW READ] parsedCount=${fileCount} paths=`, filePaths.slice(0, 15));
    overlay.append(`[host] files=${fileCount} from ${lsKey}`);

    // STEP 4: Log each file loaded into the preview
    for (const filePath of filePaths) {
      console.log(`PREVIEW LOADED: ${filePath}`);
    }
    // Log src/App.tsx content snippet to verify it's the AI-generated version
    const appTsxContent = files["src/App.tsx"];
    if (appTsxContent) {
      console.log("[__render] src/App.tsx content snippet:", appTsxContent.slice(0, 200));
    }
    bar.update({
      sessionId,
      files: fileCount,
      status: "Checking…",
      lsKey,
      paths: filePaths,
    });

    if (!files["src/App.tsx"]) {
      const keys = Object.keys(files).slice(0, 50).join("\n  ");
      const msg =
        `src/App.tsx is required. Files present:\n  ${keys}`;
      setStatus("Error");
      setErrorText(msg);
      overlay.append(`[host] ${msg}`);
      bar.update({
        sessionId,
        files: fileCount,
        status: "Error",
        error: "No src/App.tsx",
        lsKey,
      });
      showErrorInRoot("Missing src/App.tsx", msg);
      return;
    }

    const filesForBuild = { ...files };
    delete filesForBuild["src/main.tsx"];
    delete filesForBuild["src/main.ts"];
    delete filesForBuild["src/main.jsx"];
    delete filesForBuild["src/main.js"];
    delete filesForBuild["src/index.tsx"];
    delete filesForBuild["src/index.ts"];
    delete filesForBuild["src/index.jsx"];
    delete filesForBuild["src/index.js"];
    filesForBuild["src/__dayone_boot.tsx"] = BOOTSTRAP_APP_TSX;

    const entry = "src/__dayone_boot.tsx";

    // Hash includes file content via simpleHash so same-length edits still trigger rebuild
    const contentHash = simpleHash(
      Object.keys(filesForBuild).sort().map((k) => k + ":" + filesForBuild[k]).join("\0"),
    );
    const hash = entry + "|" + contentHash;
    if (hash === lastHashRef.current) return;
    lastHashRef.current = hash;

    // Reset scroll to top only on actual rebuilds (not on every poll cycle)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    setErrorText("");
    setStatus("Bundling…");
    overlay.append(`[host] bundling entry=${entry}`);
    bar.update({ sessionId, files: fileCount, status: "Bundling…", lsKey, paths: filePaths });

    bootstrapSanityCheck(BOOTSTRAP_APP_TSX);

    try {
      let buildResult;
      try {
        buildResult = await buildSandbox(filesForBuild);
      } catch (buildErr: unknown) {
        const buildMsg = buildErr instanceof Error ? buildErr.message : String(buildErr);
        setStatus("Error");
        setErrorText(buildMsg);
        overlay.append(`[host] buildSandbox threw: ${buildMsg}`);
        bar.update({ sessionId, files: fileCount, status: "Error", error: buildMsg, lsKey });
        showErrorInRoot("Build Error", buildMsg);
        return;
      }

      if (!buildResult.success) {
        setStatus("Error");
        const errMsg = buildResult.error;
        setErrorText(errMsg);
        overlay.append(`[host] build error: ${errMsg}`);
        bar.update({
          sessionId,
          files: fileCount,
          status: "Error",
          error: errMsg,
          lsKey,
        });
        if (errMsg.startsWith(EXTERNAL_DEPENDENCY_BLOCKED_PREFIX)) {
          const after = errMsg.slice(EXTERNAL_DEPENDENCY_BLOCKED_PREFIX.length);
          const pkg = after.split(".")[0]?.trim() ?? "unknown";
          showErrorInRoot(
            "External dependency not allowed",
            `The dependency "${pkg}" is not allowed in the preview.\n\nUse built-in UI components (@/components/ui) or add the dependency via builder settings.`,
          );
        } else {
          showErrorInRoot("Build Error", errMsg);
        }
        return;
      }

      overlay.append(`[host] outputFiles=${buildResult.css ? "js+css" : "js"}`);

      let preflight: { ok: boolean; errorCount?: number; firstErrors?: string[] } | undefined;
      const sid = getSessionId();
      if (sid && typeof localStorage !== "undefined") {
        try {
          const raw = localStorage.getItem(`dayone:sandbox:preflight:${sid}`);
          if (raw) preflight = JSON.parse(raw) as { ok: boolean; errorCount?: number; firstErrors?: string[] };
        } catch {
          /* ignore */
        }
      }
      postToAncestors({
        type: "DAYONE_BUILD_META",
        createdScreenStubs: buildResult.createdScreenStubs ?? [],
        preflight: preflight ?? { ok: true },
      });

      cleanupScript();

      // Crossfade: if skeleton is showing, clone it into an overlay so real app renders underneath
      const skeletonEl = document.getElementById("dayone-skeleton");
      if (skeletonEl) {
        const existing = document.getElementById("dayone-fade-overlay");
        if (existing) existing.remove();
        const fadeOverlay = document.createElement("div");
        fadeOverlay.id = "dayone-fade-overlay";
        fadeOverlay.style.cssText = "position:fixed;inset:0;z-index:2147483645;transition:opacity 300ms ease;pointer-events:none;";
        fadeOverlay.innerHTML = skeletonEl.outerHTML;
        document.documentElement.appendChild(fadeOverlay);
      }

      const { root, css } = ensureSandboxDom();
      css.textContent = buildResult.css ?? "";
      if (!window.__DAYONE_REACT_ROOT__) {
        root.innerHTML = "";
      }

      setSandboxReactGlobals();
      lastRuntimeErrorRef.current = null;
      if (typeof window !== "undefined") {
        window.__DAYONE_PREVIEW_OK__ = false;
        window.__DAYONE_PREVIEW_ERROR__ = undefined;
        window.__DAYONE_LAST_CONSOLE_ERRORS__ = [];
        window.__DAYONE_RENDERED__ = false;
        window.__DAYONE_BOOT_STEP__ = undefined;
        window.__DAYONE_LAST_ERROR__ = undefined;
      }

      // Use inline script (textContent) instead of blob URL — Safari blocks blob: in sandboxed iframes.
      const script = document.createElement("script");
      script.id = "dayone-render-script";
      script.textContent = buildResult.js;
      if (isDebugMode()) {
        console.log("[__render] script injected (inline)", { entry, format: SANDBOX_FORMAT, jsLen: buildResult.js.length });
      }

      const bootstrapContent = filesForBuild["src/__dayone_boot.tsx"] ?? BOOTSTRAP_APP_TSX;

      const runPostLoadCheck = () => {
        const HANDSHAKE_POLL_MS = 50;
        // Increased from 1500ms to 5000ms for complex apps with 40+ files
        const HANDSHAKE_MAX_MS = 5000;
        let elapsed = 0;

        const check = (): void => {
          try {
            const rootEl = document.getElementById("root");
            const ok = !!(typeof window !== "undefined" && window.__DAYONE_PREVIEW_OK__);
            const errInfo = typeof window !== "undefined" ? window.__DAYONE_PREVIEW_ERROR__ : undefined;
            const hasContent = !!(rootEl && (rootEl.children.length > 0 || rootEl.textContent?.trim()));

            if (ok || hasContent) {
              setStatus("Preview running");
              overlay.append("[host] handshake ok or root populated");
              bar.update({ sessionId, files: fileCount, status: "Running", lsKey });
              // Crossfade: fade out skeleton overlay to reveal real app
              const fadeOv = document.getElementById("dayone-fade-overlay");
              if (fadeOv) {
                fadeOv.style.opacity = "0";
                setTimeout(() => fadeOv.remove(), 350);
              }
              // Kick off AI image generation for any data-generate-image tags (async, non-blocking)
              processGeneratedImages(sessionId).catch(() => {});
              return;
            }

            if (errInfo) {
              const msg = [errInfo.message, errInfo.stack, errInfo.componentStack].filter(Boolean).join("\n\n");
              setStatus("Error");
              setErrorText(msg);
              overlay.append(`[host] ErrorBoundary caught: ${errInfo.message}`);
              bar.update({ sessionId, files: fileCount, status: "Error", error: errInfo.message.slice(0, 80), lsKey });
              showErrorInRoot("Component Error", msg);
              return;
            }

            elapsed += HANDSHAKE_POLL_MS;
            if (elapsed >= HANDSHAKE_MAX_MS) {
              const captured = lastRuntimeErrorRef.current;
              const bootStep = typeof window !== "undefined" ? window.__DAYONE_BOOT_STEP__ : undefined;
              const lastErr = typeof window !== "undefined" ? window.__DAYONE_LAST_ERROR__ : undefined;
              const consoleErrs = (typeof window !== "undefined" ? window.__DAYONE_LAST_CONSOLE_ERRORS__ : []) ?? [];
              const parts: string[] = [
                `Entry file: ${entry}`,
                bootStep != null ? `BootStep: ${bootStep}` : "",
                lastErr ? `Last error: ${lastErr}` : "",
                "",
                captured
                  ? `Captured runtime ${captured.eventType}: ${captured.message}${captured.stack ? "\n\n" + captured.stack : ""}`
                  : "No runtime error was captured.",
              ].filter(Boolean);
              if (consoleErrs.length > 0) {
                parts.push("", "Last console errors (React may log here):", "---", ...consoleErrs.slice(-5), "---");
              }
              if (bootstrapContent) {
                const lines = bootstrapContent.split("\n").slice(0, 12);
                parts.push("", "Bootstrap (first 12 lines):", "---", ...lines, "---");
              }
              const msg = parts.join("\n");
              if (isDebugMode()) {
                console.error("[__render] root empty after handshake timeout – entry:", entry, "captured:", captured, "consoleErrs:", consoleErrs.length);
              }
              setStatus("Error");
              setErrorText(msg);
              overlay.append(`[host] root empty, entry=${entry}`);
              bar.update({ sessionId, files: fileCount, status: "Error", error: "Empty root", lsKey });
              showErrorInRoot("Render Failed", msg);
              return;
            }

            setTimeout(check, HANDSHAKE_POLL_MS);
          } catch (e: unknown) {
            const errMsg = e instanceof Error ? e.message : String(e);
            setStatus("Error");
            setErrorText(errMsg);
            overlay.append(`[host] post-load check error: ${errMsg}`);
            bar.update({ sessionId, files: fileCount, status: "Error", error: errMsg, lsKey });
            showErrorInRoot("Post-Load Error", errMsg);
          }
        };

        // Script executes synchronously when appended (inline). Give React a microtask to commit.
        requestAnimationFrame(() => requestAnimationFrame(check));
      };

      script.onerror = () => {
        const consoleErrs = (typeof window !== "undefined" ? window.__DAYONE_LAST_CONSOLE_ERRORS__ : []) ?? [];
        const parts = [
          `Script failed to parse or execute. Entry: ${entry}. Format: ${SANDBOX_FORMAT} (inline classic script).`,
          "",
          "Safari may block certain patterns in sandboxed iframes. Last console output:",
          ...consoleErrs.slice(-5),
        ];
        const msg = parts.join("\n");
        if (isDebugMode()) console.error("[__render] script.onerror (inline)", { entry });
        setStatus("Error");
        setErrorText(msg);
        overlay.append(`[host] script.onerror`);
        bar.update({ sessionId, files: fileCount, status: "Error", error: "Script parse/execute failed", lsKey });
        showErrorInRoot("Script Execute Error", msg);
      };

      try {
        document.body.appendChild(script);
        runPostLoadCheck();
      } catch (scriptErr: unknown) {
        const msg = scriptErr instanceof Error ? scriptErr.message : String(scriptErr);
        setStatus("Error");
        setErrorText(msg);
        overlay.append(`[host] script execute error: ${msg}`);
        bar.update({ sessionId, files: fileCount, status: "Error", error: msg, lsKey });
        showErrorInRoot("Script Execute Error", msg);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus("Error");
      setErrorText(msg);
      overlay.append(`[host] build error: ${msg}`);
      bar.update({ sessionId, files: fileCount, status: "Error", error: msg, lsKey });
      showErrorInRoot("Build Error", msg);
    }
  }

  useEffect(() => {
    showSkeletonInRoot();
    const overlay = ensureHostOverlay();
    overlay.append("[host] mounted");

    // Install console bridge BEFORE any user code runs so all console.log/etc are forwarded
    if (sessionId) installConsoleBridge(sessionId);

    // Register error listeners BEFORE any sandbox script loads (catches script parse/runtime errors)
    const onError = (e: ErrorEvent) => {
      const msg = String(e?.message ?? e);
      const stack = e?.error && typeof (e.error as Error).stack === "string" ? (e.error as Error).stack : undefined;
      const filename = e?.filename ?? "";
      const lineno = e?.lineno ?? 0;
      const colno = e?.colno ?? 0;
      const eventType = "error";
      lastRuntimeErrorRef.current = { message: msg, stack, eventType };
      const loc = filename || lineno ? `\n  at ${filename || "?"}:${lineno}:${colno}` : "";
      console.error(`[__render] ${eventType}:`, msg, stack ?? "", loc);
      ensureHostOverlay().append(`[${eventType}] ${msg}${loc}`);
      showRuntimeErrorInRoot(msg + loc, stack, eventType);
      if (sessionId) postLog(sessionId, "error", `[${eventType}] ${msg}`);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e?.reason;
      const msg = reason instanceof Error ? reason.message : String(reason ?? "Unknown rejection");
      const stack = reason instanceof Error ? reason.stack : undefined;
      const eventType = "unhandledrejection";
      lastRuntimeErrorRef.current = { message: msg, stack, eventType };
      console.error(`[__render] ${eventType}:`, msg, stack ?? "");
      ensureHostOverlay().append(`[${eventType}] ${msg}`);
      showRuntimeErrorInRoot(msg, stack, eventType);
      if (sessionId) postLog(sessionId, "error", `[${eventType}] ${msg}`);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    rebuild();
    const i = setInterval(rebuild, 800);

    // Poll build state from localStorage to show skeleton + progress pill during AI generation
    const buildStateKey = sessionId ? `dayone:sandbox:buildstate:${sessionId}` : null;
    const pill = ensureProgressPill();
    const checkBuildState = () => {
      if (!buildStateKey) return;
      try {
        const raw = localStorage.getItem(buildStateKey);
        if (!raw) { pill.hide(); return; }
        const state = JSON.parse(raw) as { phase: string; completed: number; total: number; ts: number };
        // Ignore stale entries (>120s old)
        if (Date.now() - (state.ts || 0) > 120_000) {
          localStorage.removeItem(buildStateKey);
          pill.hide();
          return;
        }
        if (state.phase === "generating") {
          // Don't overwrite error display with skeleton
          if (!document.getElementById("dayone-skeleton") && !document.getElementById("dayone-error")) {
            showSkeletonInRoot();
          }
          const total = state.total > 0 ? `/${state.total}` : "";
          pill.show(`Building... ${state.completed}${total} files`);
        }
      } catch { /* ignore */ }
    };
    const buildStateInterval = setInterval(checkBuildState, 400);

    return () => {
      clearInterval(i);
      clearInterval(buildStateInterval);
      cleanupScript();
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  void status;
  void errorText;
  return null;
}
